/**
 * useHealthStatus Hook
 * Phase 7.16: Navigation Health Status Indicator
 *
 * Polls GET /health every 60 s and measures API response time client-side.
 * The endpoint is unauthenticated — no auth token is attached.
 */

'use client';

import { useCallback, useEffect, useState } from 'react';

export type HealthStatus = 'checking' | 'healthy' | 'degraded' | 'unhealthy';

export interface DatabaseHealth {
  status: 'healthy' | 'unhealthy' | 'resuming' | 'unknown';
  latencyMs: number | null;
}

export interface HealthState {
  status: HealthStatus;
  apiResponseTimeMs: number | null;
  database: DatabaseHealth;
  lastChecked: Date | null;
}

const POLL_INTERVAL_MS = 60_000;
const FETCH_TIMEOUT_MS = 10_000;

const healthUrl = (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001/api/v1')
  .replace(/\/api\/v1\/?$/, '') + '/health';

const initialState: HealthState = {
  status: 'checking',
  apiResponseTimeMs: null,
  database: { status: 'unknown', latencyMs: null },
  lastChecked: null,
};

function mapStatus(httpStatus: number, bodyStatus: string): HealthStatus {
  if (httpStatus === 200 && bodyStatus === 'healthy') return 'healthy';
  if (httpStatus === 503 && bodyStatus === 'degraded') return 'degraded';
  if (httpStatus >= 500) return 'unhealthy';
  if (bodyStatus === 'unhealthy') return 'unhealthy';
  return 'unhealthy';
}

function mapDbStatus(db: { status?: string; resuming?: boolean; latency_ms?: number } | undefined): DatabaseHealth['status'] {
  if (!db) return 'unknown';
  if (db.resuming === true) return 'resuming';
  if (db.status === 'healthy') return 'healthy';
  if (db.status === 'unhealthy') return 'unhealthy';
  return 'unknown';
}

export function useHealthStatus(): HealthState {
  const [state, setState] = useState<HealthState>(initialState);

  const poll = useCallback(async (): Promise<void> => {
    const start = performance.now();
    try {
      const response = await fetch(healthUrl, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
      const apiResponseTimeMs = Math.round(performance.now() - start);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body = await response.json() as Record<string, any>;

      setState({
        status: mapStatus(response.status, String(body.status ?? '')),
        apiResponseTimeMs,
        database: {
          status: mapDbStatus(body.checks?.database as { status?: string; resuming?: boolean; latency_ms?: number } | undefined),
          latencyMs: (body.checks?.database?.latency_ms as number | undefined) ?? null,
        },
        lastChecked: new Date(),
      });
    } catch {
      setState(prev => ({ ...prev, status: 'unhealthy', lastChecked: new Date() }));
    }
  }, []);

  useEffect(() => {
    void poll();
    const id = setInterval(() => { void poll(); }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [poll]);

  return state;
}
